import { type ReactNode, useEffect, useId, useRef } from 'react'
import { LayoutGroup, LazyMotion, m } from 'motion/react'
import cx from 'classnames'
import styles from './Tabs.module.css'
import { RawLink } from '@/components/Link'
import type { PressEventHandler } from '@/infra/prop-types'
import { type To, useMatch, useResolvedPath } from 'react-router'
import { match } from '@/utils/pattern-match-util'
import { ContentFader } from '@/components/ContentFader'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { getScrollParent } from '@react-aria/utils'
import { getScrollLeftForCentering } from '@/utils/browser-util'
import { indicatorSpring } from '@/consts/springs'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

export interface TabListProps {
  children?: ReactNode

  /**
   * If set, then the tabs will be considered a navigation element
   * for accessibility purposes.
   */
  role?: 'navigation'

  /**
   * The type of layout to use. `static` makes the tabs appear with a fixed
   * gap between them. `stretch` will adjust the gap based on the available
   * space, making the content stretch to the available width. `auto` toggles
   * between the others based on the viewport size.
   * Default is `auto`.
   */
  layout?: 'auto' | 'stretch' | 'static'

  /**
   * Clip margin to apply on the X-axis for the content fader.
   */
  fadeClipMarginX?: string | number

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Tab bar contains a set of tabs.
 * Only one tab may be active at a time.
 */
export function TabList({
  children,
  role,
  fadeClipMarginX = 'var(--spacing-3xl)',
  layout = 'auto',
  testId,
}: TabListProps) {
  const id = useId()
  const isSpacious = useIsSpacious()

  return (
    <LazyMotion features={loadMotionFeatures}>
      <ContentFader
        fadeEnd={24}
        clipMargin={{
          x: fadeClipMarginX,
          // Make space for the underline
          y: '2px',
        }}
      >
        <div role={role} data-test={testId} className={styles.root}>
          <m.ol
            layout
            layoutRoot
            className={cx(
              styles.list,
              match(layout, {
                static: styles.static,
                stretch: styles.stretch,
                auto: isSpacious ? styles.static : styles.stretch,
              }),
            )}
          >
            <LayoutGroup id={id}>{children}</LayoutGroup>
          </m.ol>
        </div>
      </ContentFader>
    </LazyMotion>
  )
}

interface TabProps {
  children?: ReactNode

  /**
   * The tab's label/title.
   */
  label: string

  /**
   * The route to navigate to. The tab is considered active if
   * this route matches.
   */
  to?: To

  /**
   * If {@link TabListProps.to} is specified, then the route will be replaced
   * instead of pushed to history.
   */
  replace?: boolean

  /**
   * Whether the tab is active, if not a link.
   */
  active?: boolean

  /**
   * Press handler, if the tab is not a link.
   */
  onPress?: PressEventHandler

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Tab item.
 */
export function Tab({
  children,
  label,
  active,
  onPress,
  to,
  replace,
  testId,
}: TabProps) {
  // Checks whether the tab is active based on the pathname.
  const resolved = useResolvedPath(to ?? '/')
  const activeBasedOnRoute = !!useMatch(resolved.pathname)

  // Only use the active state from the route if we're using a `to` prop.
  active = (to ? activeBasedOnRoute : active) ?? false

  const ref = useRef<HTMLLIElement>(null)

  // When a tab becomes active, scroll it into view.
  useEffect(() => {
    const tabElement = ref.current
    if (!active || !tabElement) {
      return
    }

    // The scroll container is the content fader.
    const parent = getScrollParent(tabElement)

    if (parent.scrollWidth === parent.clientWidth) {
      // Not overflowing.
      return
    }

    // Calculate the new "scroll left", ensuring the active
    // tab scrolls to be centered.
    // NOTE: We're not using `scrollIntoView` because it scrolls
    // the entire viewport, not just the scrolling element.
    const newScrollLeft = getScrollLeftForCentering(parent, tabElement)
    const frame = requestAnimationFrame(() => {
      parent.scrollLeft = newScrollLeft
    })
    return () => cancelAnimationFrame(frame)
  }, [active])

  return (
    <m.li layout layoutRoot className={styles.item} ref={ref}>
      <RawLink
        to={to}
        onPress={onPress}
        replace={replace}
        testId={testId}
        className={({ isFocusVisible, isHovered }) =>
          cx(
            active ? styles.active : styles.inactive,
            isFocusVisible && styles.focusVisible,
            isHovered && styles.hover,
          )
        }
      >
        <div className={styles.tabContent}>
          <span className={styles.tabLabel}>{label}</span>
          {children}
        </div>
        {active && (
          <m.div
            layout
            layoutId="underline"
            aria-hidden
            className={styles.underline}
            // Replace the `y` coordinate in the generated transform to avoid
            // the underlying flying all over the place when the page scrolls
            // due to a tab change.
            transformTemplate={(_, generated) =>
              generated.replace(
                /(translate3d\([-\d.]+px, )([-\d.]+)/,
                (_match, prefix: string, _: string) => `${prefix}0`,
              )
            }
            transition={indicatorSpring}
          />
        )}
      </RawLink>
    </m.li>
  )
}
